import * as angular from "angular";
import { Effort } from "../../models/effort.model";
import { ChartHelper } from "./chart.helper";
import { ResultDataProvider } from "../../data-provider/result.dp";
import { DashboardWidget } from "../../models/dashboard-widget.model";
import { DashboardWidgetFilter } from "../../models/dashboard-widget-filter.model";
import { DashboardDataProvider } from "../../data-provider/dashboard.dp";

let DataChartComponent = {
    selector: "vcDataChart",
    templateUrl: "/angular/components/result/data-chart.component.html",
    bindings: {
        widget: "=",
        filter: "=?",
        type: "=?",
    },
    controller: class DataChartController {
        // * bindings for typescript compiler *
        public widget?: DashboardWidget;
        public filter?: DashboardWidgetFilter;
        public type: string;
        // ************************************

        private readonly $timeout: any;

        public dataLoading: boolean = true;
        public data: any;
        public chartData: any;
        public chartReadyCallback: any;
        public dataProvider: ResultDataProvider;
        public dashboardDataProvider: DashboardDataProvider;
        public effort: Effort;
        public chartHelper: ChartHelper;
        public options: any;
        public elemIdentifier: string;

        public containerElement: any;
        public containerHeight: number;
        public containerWidth: number;

        constructor(
            $scope: any,
            $timeout: any,
            __env: any,
            DashboardDataProvider: DashboardDataProvider,
            ChartHelper: ChartHelper,
            ResultDataProvider: ResultDataProvider
        ) {
            this.$timeout = $timeout;
            this.chartHelper = ChartHelper;
            this.dataProvider = ResultDataProvider;
            this.dashboardDataProvider = DashboardDataProvider;
            this.elemIdentifier =
                "chart-container-available-space" + Math.random() * 100000;

            $scope.$watch(
                () => this.filter,
                (
                    newValue: DashboardWidgetFilter,
                    oldValue: DashboardWidgetFilter
                ): void => {
                    if (oldValue && newValue !== oldValue && this.type) {
                        this.loadData();
                    }
                },
                true
            );

            $scope.$watch(
                () => this.widget,
                (newValue: DashboardWidget, oldValue: DashboardWidget) => {
                    if (
                        this.dashboardDataProvider.widgetChangeRequiresReload(
                            newValue,
                            oldValue
                        )
                    ) {
                        this.loadData();
                    }
                },
                true
            );

            $scope.$watch(
                () => this.widget.style.chartOptions,
                (newValue: any, oldValue: any): void => {
                    if (
                        oldValue &&
                        JSON.stringify(newValue) !== JSON.stringify(oldValue) &&
                        newValue.stacked == oldValue.stacked
                    ) {
                        this.setChartOptions();
                    }
                },
                true
            );

            $scope.$watch(
                () => this.widget.style.legend,
                (newValue: any, oldValue: any): void => {
                    if (
                        oldValue &&
                        JSON.stringify(newValue) !== JSON.stringify(oldValue)
                    ) {
                        this.setChartOptions();
                    }
                },
                true
            );

            $scope.$watch(
                () => this.widget.style.fontSize,
                (newValue: number, oldValue: number): void => {
                    if (
                        oldValue &&
                        JSON.stringify(newValue) !== JSON.stringify(oldValue)
                    ) {
                        this.setChartOptions();
                    }
                },
                true
            );

            $scope.$on("resized", (): void => {
                this.resizeIfRequired();
            });
        }

        $onInit() {
            if (!this.widget.style || !this.widget.style)
                this.widget.style.chartType = "pie";

            this.containerElement = document.getElementById(
                this.elemIdentifier
            );

            this.loadData();
        }

        resizeIfRequired(): void {
            this.$timeout((): void => {
                if (this.sizeHasChanged()) {
                    this.resizeChart();
                }
            }, 100);
        }

        setChartOptions(): void {
            this.options.chart.showLabels =
                this.widget.style.chartOptions.showLabels;

            this.$timeout((): void => {
                this.resizeChart();
            }, 100);
        }

        sizeHasChanged(): boolean {
            this.containerElement = document.getElementById(
                this.elemIdentifier
            );

            if (this.containerElement) {
                return (
                    this.containerHeight !=
                        this.containerElement.clientHeight ||
                    this.containerWidth != this.containerElement.clientWidth
                );
            }

            return false;
        }

        resizeChart(): void {
            this.containerElement = document.getElementById(
                this.elemIdentifier
            );

            if (this.containerElement) {
                this.containerHeight = this.containerElement.clientHeight;
                this.containerWidth = this.containerElement.clientWidth;
                let height =
                    this.containerElement.clientHeight !=
                    this.options.chart.height
                        ? this.containerElement.clientHeight
                        : this.containerElement.clientHeight - 1;

                this.options.chart.height = height < 280 ? 280 : height;
                this.options.chart.widget =
                    this.containerWidth < 150 ? 150 : this.containerWidth;

                //If a bar chart, check if labels will overlap and rotate them if necessary
                if (
                    this.widget.style.chartType === "bar" &&
                    this.data &&
                    this.containerWidth
                ) {
                    if (!this.options.chart.xAxis) {
                        this.options.chart.xAxis = {};
                    }

                    if (
                        this.chartHelper.shouldRotateLabels(
                            this.data,
                            this.containerWidth
                        )
                    ) {
                        this.options.chart.xAxis.rotateLabels = -45;
                        this.options.chart.margin.bottom = 100;
                    } else {
                        if (this.options.chart.xAxis.rotateLabels !== 0) {
                            this.options.chart.xAxis.rotateLabels = 0;
                            this.options.chart.margin.bottom = 50;
                        }
                    }
                }
            }
        }

        displayAsRow(): boolean {
            if (["pie", "donut", "bar"].includes(this.widget.style.chartType)) {
                return ["left", "right"].includes(
                    this.widget.style.legend.position
                );
            }
            return false;
        }

        hasData(): boolean {
            if (["pie", "donut"].includes(this.widget.style.chartType)) {
                let anyData = false;
                if (this.data) {
                    angular.forEach(this.data, (field) => {
                        if (field.y > 0) {
                            anyData = true;
                        }
                    });
                }
                return anyData;
            } else if (this.widget.style.chartType === "bar") {
                let anyData: boolean = false;
                if (this.data) {
                    angular.forEach(this.data, (field) => {
                        if (field.y && field.y > 0) {
                            anyData = true;
                        } else if (field.values) {
                            angular.forEach(field.values, (value) => {
                                if (value.y > 0) {
                                    anyData = true;
                                }
                            });
                        }
                    });
                }
                return anyData;
            } else {
                return this.data && this.options;
            }
        }

        loadData(): void {
            this.options = this.chartHelper.buildChartOptions(
                this.widget,
                (event: any) => {
                    if (event.key == "Stacked") {
                        this.widget.style.chartOptions.stacked = true;
                        this.dashboardDataProvider.updateWidget(this.widget);
                    } else if (event.key == "Grouped") {
                        this.widget.style.chartOptions.stacked = false;
                        this.dashboardDataProvider.updateWidget(this.widget);
                    }
                }
            );
            this.setChartOptions();

            this.dataLoading = true;

            this.dataProvider.chartData(this.widget, this.filter).then(
                (data: any): void => {
                    if (this.widget.overtime) {
                        this.chartData = data;
                        this.data = data;

                        this.options.chart.color =
                            this.chartHelper.generatePalette(
                                this.widget.style.colorPalette["colors"],
                                data.length
                            );

                        this.chartHelper.setDateAxis(
                            this.options.chart,
                            this.data
                        );
                    } else {
                        let structuredData = ["pie", "donut"].includes(
                            this.widget.style.chartType
                        )
                            ? data
                            : data[0].values;

                        this.options.chart.color =
                            this.chartHelper.generatePalette(
                                this.widget.style.colorPalette["colors"],
                                structuredData.length
                            );

                        this.chartData = data;
                        this.data = structuredData;
                    }

                    this.$timeout(() => {
                        this.dataLoading = false;

                        this.$timeout(() => {
                            this.resizeChart();
                        }, 50);
                    }, 100);
                },
                (error: any): void => {
                    this.data = null;
                    this.dataLoading = false;
                }
            );
        }
    },
};

angular
    .module("result")
    .component(DataChartComponent.selector, DataChartComponent);
